import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import api, { imageBaseUrl } from '../services/api';

const SettingsContext = createContext();

export const useSettings = () => {
    const context = useContext(SettingsContext);
    if (!context) {
        throw new Error('useSettings must be used within a SettingsProvider');
    }
    return context;
};

export const SettingsProvider = ({ children }) => {
    const [settings, setSettings] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(false);
    const [logoBase64, setLogoBase64] = useState(null);

    const fetchSettings = useCallback(async (silent = false) => {
        if (!silent) setLoading(true);
        setError(false);
        try {
            const response = await api.get('/settings');
            const data = response.data;
            setSettings(data);
            
            // Update browser title if school name exists
            if (data?.school_name) {
                document.title = data.school_name;
            }

            // Convert logo to base64 if it exists
            if (data?.logo_url) {
                try {
                    const logoFullUrl = `${imageBaseUrl}${data.logo_url}`;
                    const imgResponse = await fetch(logoFullUrl);
                    const blob = await imgResponse.blob();
                    const reader = new FileReader();
                    reader.onloadend = () => {
                        setLogoBase64(reader.result);
                    };
                    reader.readAsDataURL(blob);
                } catch (imgError) {
                    console.error('Failed to convert logo to base64:', imgError);
                    setLogoBase64(null);
                }
            } else {
                setLogoBase64(null);
            }

        } catch (error) {
            console.error('Failed to fetch school settings:', error);
            setError(true);
        } finally {
            if (!silent) setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchSettings();
    }, [fetchSettings]);

    // Automatic polling when in error state
    useEffect(() => {
        let interval;
        if (error) {
            interval = setInterval(() => {
                fetchSettings(true); // Silent fetch
            }, 10000); // Check every 10 seconds
        }
        return () => {
            if (interval) clearInterval(interval);
        };
    }, [error, fetchSettings]);

    const value = {
        settings,
        loading,
        error,
        refreshSettings: fetchSettings,
        schoolName: settings?.school_name || 'School Management System',
        schoolLogo: settings?.logo_url,
        schoolLogoWide: settings?.logo_wide_url,
        logoBase64,
    };

    return (
        <SettingsContext.Provider value={value}>
            {children}
        </SettingsContext.Provider>
    );
};
